// Shared leave + bank-holiday helpers (Karan 23/07). Used by the Calendar (balance
// + overlay), the Timesheet (blank non-working days, reduce required hours) and
// Home. Leave year runs 1 Nov → 31 Oct; entitlement is 21 working days.
//
// Bank holidays are ENGLAND & WALES (gov.uk/bank-holidays). Refresh yearly — or
// later, sync from org settings.bankHolidays so the server can keep them current.
// NOTE: UK-only; India-based staff have a different calendar (out of scope for v1).

export const ENTITLEMENT_DAYS = 21;

export const BANK_HOLIDAYS = new Set([
  // 2025
  '2025-01-01', '2025-04-18', '2025-04-21', '2025-05-05', '2025-05-26', '2025-08-25', '2025-12-25', '2025-12-26',
  // 2026
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
  // 2027
  '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
]);

/**
 * The words for a kind of time off (v4.5.0). 'off' is what a colleague sees
 * of someone else's sick day — the kind is never shown to them (firestore.rules).
 */
export function leaveWord(kind, { short = false } = {}) {
  if (kind === 'sick') return short ? 'Sick' : 'Sick leave';
  if (kind === 'other') return 'Time off';
  if (kind === 'off') return 'Off';
  if (kind === 'wfh') return 'Working from home';
  if (kind === 'cpd') return 'CPD';
  return 'Annual leave';
}

// Local ISO date (yyyy-mm-dd) — avoids the UTC shift toISOString() causes.
export function isoOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
export function isBankHoliday(iso) { return BANK_HOLIDAYS.has(iso); }
export function isWeekend(d) { const g = d.getDay(); return g === 0 || g === 6; }

// A working day = Mon–Fri and not a bank holiday.
export function isWorkingDay(d) { return !isWeekend(d) && !BANK_HOLIDAYS.has(isoOf(d)); }

// Count working days between two ISO dates, inclusive.
export function workingDaysBetween(fromIso, toIso) {
  if (!fromIso) return 0;
  const start = new Date(fromIso + 'T00:00:00');
  const end = new Date((toIso || fromIso) + 'T00:00:00');
  if (end < start) return 0;
  let n = 0;
  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) if (isWorkingDay(d)) n++;
  return n;
}

// The number of days a single leave request costs the allowance (half-day aware).
export function leaveDays(req) {
  if (req == null) return 0;
  if (typeof req.days === 'number') return req.days;          // explicit override wins
  const wd = workingDaysBetween(req.from, req.to);
  if (req.halfDay && wd <= 1) return 0.5;                     // half day only meaningful on a single day
  return wd;
}

// Leave year containing `date`: 1 Nov → 31 Oct. Returns ISO bounds + a label.
export function leaveYearBounds(date = new Date()) {
  const y = date.getMonth() >= 10 ? date.getFullYear() : date.getFullYear() - 1; // Nov(10)+ → this year, else prev
  return {
    startIso: `${y}-11-01`,
    endIso: `${y + 1}-10-31`,
    label: `1 Nov ${y} – 31 Oct ${y + 1}`,
  };
}

// True if a leave request overlaps the given leave year at all.
function overlapsYear(req, bounds) {
  return req.from && req.from <= bounds.endIso && (req.to || req.from) >= bounds.startIso;
}

/**
 * Balance for one person in the leave year containing refDate.
 * `requests` = raw leaveRequests; only that user's are counted.
 * `taken` counts APPROVED leave; `pending` is requested-but-not-approved (shown separately).
 */
export function leaveBalance(requests, userEmail, refDate = new Date(), entitlement = ENTITLEMENT_DAYS) {
  const ent = Number(entitlement) > 0 ? Number(entitlement) : ENTITLEMENT_DAYS; // per-person allowance (Team page); 21 default
  const bounds = leaveYearBounds(refDate);
  const mine = (requests || []).filter((r) => r.userEmail === userEmail
    && (r.kind || 'annual') === 'annual' && overlapsYear(r, bounds));
  let taken = 0, pending = 0;
  for (const r of mine) {
    const days = leaveDays(r);
    if (r.status === 'approved') taken += days;
    else if (r.status === 'requested') pending += days;
  }
  return {
    entitlement: ent,
    taken: +taken.toFixed(1),
    pending: +pending.toFixed(1),
    remaining: +(ent - taken).toFixed(1),
    remainingAfterPending: +(ent - taken - pending).toFixed(1),
    year: bounds.label,
  };
}

// Approved leave (annual or sick) covering a given ISO day, for the timesheet to
// blank out. Returns the matching request or null.
export function leaveOnDay(requests, userEmail, iso) {
  return (requests || []).find((r) => r.userEmail === userEmail && r.status === 'approved' && r.kind !== 'wfh'
    && r.from && r.from <= iso && (r.to || r.from) >= iso) || null;
}
